import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  DashboardSummary,
  StockOnHandResult,
  AdjustmentsResult,
  ReconciliationResult,
  BatchBalancesResult,
  SlowMovingResult
} from '../models/warehouse-reporting.model';

/**
 * Client for the backend's warehousing report aggregates.
 *
 * Every route is a POST with a JSON body, matching how `reporting/utilisation` works
 * elsewhere in the ERP, and an empty body is valid on all of them. Filters are omitted
 * rather than sent empty — the backend 400s on anything it cannot parse instead of quietly
 * ignoring it, which is the behaviour we want.
 *
 * Dates go up as plain `YYYY-MM-DD`. Do NOT widen an end date to 23:59:59 here: the backend
 * already extends a bare `to` to the end of that day, and doing it at both ends once meant
 * the range was applied twice.
 */
@Injectable({
  providedIn: 'root'
})
export class WarehouseReportingService {

  /**
  * Endpoint for http calls
  */
  private _endpoint: string = environment.api.endpoint;

  constructor(private httpClient: HttpClient) { }

  getDashboardSummary(warehouseId: number = -1): Observable<DashboardSummary> {
    return this.post('dashboard-summary', this.scope(warehouseId)).pipe(map((data: any) => {
      return <DashboardSummary>data;
    }));
  }

  /**
   * Stock on hand, replayed from the ledger rather than read off the cached levels.
   *
   * `includeZero` defaults to TRUE here, which is not the endpoint's own default. An emptied
   * shelf keeps its row so its history survives, and the screens have always listed it; leaving
   * the endpoint's default in place silently dropped that row from the report - six rows became
   * five with nothing to say one had gone. Pass `includeZero: false` deliberately if a caller
   * really wants only occupied locations.
   *
   * -1 is the screens' "all" and is stripped rather than sent: the backend would read it as a
   * genuine id and match nothing.
   *
   * @param filters warehouseId / warehouseLocationId / productId / includeZero / limit / offset
   */
  getStockOnHand(filters: any = {}): Observable<StockOnHandResult> {
    const body: any = { includeZero: true };

    ['warehouseId', 'warehouseLocationId', 'productId'].forEach((key: string) => {
      const value = Number(filters ? filters[key] : -1);

      if (value && value !== -1 && !isNaN(value)) {
        body[key] = value;
      }
    });

    ['includeZero', 'limit', 'offset'].forEach((key: string) => {
      if (filters && filters[key] !== undefined && filters[key] !== null) {
        body[key] = filters[key];
      }
    });

    return this.post('stock-on-hand', body).pipe(map((data: any) => {
      return <StockOnHandResult>data;
    }));
  }

  /**
   * Adjustments by category, not merely by movement type.
   *
   * This is why the screen must not build the report from `product-stock-movement` itself:
   * stock is also written off as a goods-out against SHORT / OVER / DAMAGED / STOCKTAKE /
   * DISPOSAL. Measured on the dev data, filtering on `movementType = 'adjustment'` finds 2
   * movements where this endpoint finds 4 — so the naive version understates write-offs by
   * half, and the write-off figure is what backs an insurance claim.
   */
  getAdjustments(filters: any = {}): Observable<AdjustmentsResult> {
    return this.post('adjustments', filters).pipe(map((data: any) => {
      return <AdjustmentsResult>data;
    }));
  }

  /**
   * Stored level against the level replayed from the ledger.
   *
   * Variance-only by design: `checked` counts the pairs examined, `balanced` says whether
   * they all agreed, and `discrepancies` carries only the rows that did not — with the
   * scaled float tolerance already applied, so every row is a real defect.
   */
  getReconciliation(warehouseId: number = -1, productId: number = -1): Observable<ReconciliationResult> {
    const body: any = this.scope(warehouseId);

    if (productId && productId !== -1) {
      body.productId = productId;
    }

    return this.post('reconciliation', body).pipe(map((data: any) => {
      return <ReconciliationResult>data;
    }));
  }

  /**
   * Stock sitting with no movement in a while — the aging / dead-stock question.
   *
   * `days` defaults to 90 on the backend if omitted here; sent only when the screen has a real
   * number to send, same reasoning as every other filter in this service. -1 is stripped from the
   * scope filters exactly as `getStockOnHand` strips it, since the backend reads an absent id as
   * "all" and a literal -1 as a request for a product that does not exist.
   *
   * @param filters warehouseId / warehouseLocationId / productId / days / limit
   */
  getSlowMovingStock(filters: any = {}): Observable<SlowMovingResult> {
    const body: any = {};

    ['warehouseId', 'warehouseLocationId', 'productId'].forEach((key: string) => {
      const value = Number(filters ? filters[key] : -1);

      if (value && value !== -1 && !isNaN(value)) {
        body[key] = value;
      }
    });

    ['days', 'limit'].forEach((key: string) => {
      if (filters && filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        body[key] = filters[key];
      }
    });

    return this.post('slow-moving', body).pipe(map((data: any) => {
      return <SlowMovingResult>data;
    }));
  }

  /**
   * Batch traceability: where each batch is, what it still holds, and how much of each location's
   * stock carries no batch number at all.
   *
   * -1 is stripped rather than sent, and here that matters more than usual: `optionalId` refuses a
   * non-positive id with a 400 rather than treating it as "all", so a -1 left in would fail the whole
   * report instead of widening it.
   *
   * `batchNumber` is sent trimmed and only when non-empty, so a screen with a cleared search box asks
   * for every batch rather than for the one numbered "".
   *
   * @param filters productId / batchNumber / warehouseId / warehouseLocationId
   */
  getBatchBalances(filters: any = {}): Observable<BatchBalancesResult> {
    const body: any = {};

    ['productId', 'warehouseId', 'warehouseLocationId'].forEach((key: string) => {
      const value = Number(filters ? filters[key] : -1);

      if (value && value !== -1 && !isNaN(value)) {
        body[key] = value;
      }
    });

    const batchNumber: string = filters && filters.batchNumber ? String(filters.batchNumber).trim() : '';

    if (batchNumber) {
      body.batchNumber = batchNumber;
    }

    return this.post('batch-balances', body).pipe(map((data: any) => {
      return <BatchBalancesResult>data;
    }));
  }

  /**
   * The batch numbers already recorded against a product, for the goods-in suggestion list.
   *
   * `productId` is always sent, never omitted. The endpoint reads an absent productId as "every
   * product", so omitting it would not widen a per-product suggestion list, it would fill it with
   * numbers belonging to other products' goods — and since nothing pairs a number to a product, a
   * suggestion taken from that list would be stored without complaint.
   *
   * Returns the bare array. The endpoint wraps it as `{batchNumbers: [...]}`; unwrapping here keeps
   * the shape of the payload out of the component, which wants a list of strings to suggest.
   *
   * Suggestions only, and the caller must treat them that way. There is no batch table, so this is a
   * record of what has been typed before rather than a set of permitted values: a number absent from
   * this list is not invalid, it is a pallet numbered for the first time.
   */
  getBatchNumbers(productId: number, warehouseLocationId: number = -1): Observable<string[]> {
    const body: any = { productId: productId };

    if (warehouseLocationId && warehouseLocationId !== -1) {
      body.warehouseLocationId = warehouseLocationId;
    }

    return this.post('batch-numbers', body).pipe(map((data: any) => {
      return data && data.batchNumbers ? <string[]>data.batchNumbers : [];
    }));
  }

  /**
   * One batch traced through the ledger, in the order it was posted, with its closing balances.
   *
   * `batchNumber` is required and is sent unnormalised: the endpoint 400s without it rather than
   * tracing everything, because a traceability report that quietly widened to every batch would be
   * answering a question nobody asked. The server normalises it through the same function the posting
   * path uses, which is what makes a search for `a1` find stock booked in as `A1` — so this must not
   * normalise it first and must not send a cleared box as ''.
   *
   * @param filters batchNumber (required) / productId / warehouseId / warehouseLocationId / from / to
   */
  getBatchTraceability(filters: any = {}): Observable<any> {
    const body: any = {};

    ['productId', 'warehouseId', 'warehouseLocationId'].forEach((key: string) => {
      const value = Number(filters ? filters[key] : -1);

      if (value && value !== -1 && !isNaN(value)) {
        body[key] = value;
      }
    });

    ['from', 'to'].forEach((key: string) => {
      if (filters && filters[key]) {
        body[key] = filters[key];
      }
    });

    const batchNumber: string = filters && filters.batchNumber ? String(filters.batchNumber).trim() : '';

    if (batchNumber) {
      body.batchNumber = batchNumber;
    }

    return this.post('batch-traceability', body).pipe(map((data: any) => {
      return data;
    }));
  }

  /** -1 is the screens' "all", but the backend wants the key absent rather than -1. */
  private scope(warehouseId: number): any {
    return warehouseId && warehouseId !== -1 ? { warehouseId: warehouseId } : {};
  }

  private post(action: string, body: any): Observable<any> {
    return this.httpClient.post(this._endpoint + 'warehouse-reporting/' + action + '/',
      JSON.stringify(body ? body : {}),
      { headers: { 'Content-Type': 'application/json' } });
  }
}
